import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import PdfLayout from "../pdf/PdfLayout";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
interface Choice {
  title: string;
  detail: string;
}

interface Tool {
  activities: Choice[];
  courseName: string;
  prompt: string;
  gradeLevel?: number | string;
  imageUrl?: string;
  topic?: string;
  headerTextColor?: string;
  title?: string;
  createdAt?: string;
  authorName?: string;
}

interface PageHeaderProps {
  courseName: string;
  prompt: string;
  gradeLevel: string;
  imageUrl?: string;
  topic?: string;
  headerTextColor?: string;
}

interface ChoiceBoardDocumentProps {
  tool: Tool;
  customizationSettings: PdfCustomizationSettings;
}

Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: "white",
  },
  activitiesContainer: {
    flexGrow: 1,
  },
  rowContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 12,
  },
  choiceContainer: {
    flex: 1,
    minWidth: "45%",
    padding: 10,
    backgroundColor: "#eff6ff",
    borderRadius: 6,
  },
  title: {
    fontSize: 12,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#2563eb",
    marginBottom: 8,
  },
  detail: {
    fontSize: 10,
    fontFamily: "Montserrat",
    color: "#374151",
    lineHeight: 1.4,
  },
  activityLabel: {
    fontSize: 11,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#0891b2",
    marginBottom: 4,
  },
});

const simplifyMarkdown = (content: string): string => {
  if (!content) return "";
  return content
    .replace(/#{1,6}\s/g, "")
    .replace(/\*\*/g, "")
    .replace(/\*/g, "")
    .replace(/`/g, "")
    .replace(/\[(.*?)\]\(.*?\)/g, "$1")
    .trim();
};

const ChoiceBoardDocument = ({
  tool,
  customizationSettings,
}: ChoiceBoardDocumentProps) => {
  const activities = tool.activities || [];
  const { t } = useTranslation();

  const headerProps: PageHeaderProps = {
    courseName: tool.courseName || "",
    prompt: tool.prompt || "",
    gradeLevel: tool.gradeLevel?.toString() || "",
    imageUrl: tool.imageUrl,
    topic: tool.title || tool.prompt || "",
    headerTextColor: tool.headerTextColor,
  };

  // Aktiviteleri ikişerli gruplara ayırma
  const rows = activities.reduce((acc: Choice[][], curr, i) => {
    if (i % 2 === 0) acc.push([curr]);
    else acc[acc.length - 1].push(curr);
    return acc;
  }, []);

  return (
    <PdfLayout
      {...headerProps}
      headerTextColor="#16a34a"
      customizationSettings={customizationSettings}
      author={tool.authorName}
      createdDate={tool.createdAt}
    >
      <View style={styles.activitiesContainer}>
        {rows.map((row, rowIndex) => (
          <View wrap={false} key={rowIndex} style={styles.rowContainer}>
            {row.map((choice, index) => (
              <View style={styles.choiceContainer} key={index}>
                <Text
                  style={[
                    styles.title,
                    {
                      fontSize: scaleFontSize(
                        styles.title.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {simplifyMarkdown(choice.title)}
                </Text>
                <Text
                  style={[
                    styles.activityLabel,
                    {
                      fontSize: scaleFontSize(
                        styles.activityLabel.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("choiceBoards.activity")}
                </Text>
                <Text
                  style={[
                    styles.detail,
                    {
                      fontSize: scaleFontSize(
                        styles.detail.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {simplifyMarkdown(choice.detail)}
                </Text>
              </View>
            ))}
          </View>
        ))}
      </View>
    </PdfLayout>
  );
};

const ChoiceBoardCardPDF = ({
  tool,
  customizationSettings,
}: ChoiceBoardDocumentProps) => {
  return (
    <ChoiceBoardDocument
      tool={tool}
      customizationSettings={customizationSettings}
    />
  );
};

export default ChoiceBoardCardPDF;
